import React, {ChangeEvent, useRef, useState} from 'react';
import * as Papa from 'papaparse';
import StateButton from "../Shared/StateButton/StateButton";
import uploadIcon from "../../images/upload.svg";
import { portalCardSurface, portalOutlineControl, portalPageSubtitle, portalSecondaryPillButton } from "../../theme/portalLayout";

interface MemberFileUploadProps {
    onUpload: (file: File) => Promise<void>;
    setError: (message: string) => void;
    uploading: boolean;
    disabled: boolean;
    error: string;
    isEmptyFile: boolean;
    clearError: () => void;
}

type Row = {
    ID?: string;
    FirstName?: string;
    LastName?: string;
    Email?: string;
    DateOfBirth?: string;
};

const MAX_SIZE_MB = 100;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const expectedHeadersStrict: string[] = ["ID", "FirstName", "LastName", "Email", "DateOfBirth"];
const stripBOM = (s: string): string => s.replace(/^\uFEFF/, "");

const transformHeaderClean = (header: string): string => {
    const cleaned = stripBOM(header).trim();
    const match = expectedHeadersStrict.find(h => h.toLowerCase() === cleaned.toLowerCase());
    return match ?? cleaned;
};

function headersMatchStrict(actual: string[]): boolean {
    if (actual.length !== expectedHeadersStrict.length) return false;
    return actual.every((h: string, idx: number) => h === expectedHeadersStrict[idx]);
}

function validateRow(row: Row, index: number): string[] {
    const errors: string[] = [];

    const email = (row.Email ?? "").trim();
    const firstName = (row.FirstName ?? "").trim();
    const lastName = (row.LastName ?? "").trim();

    if (!email) errors.push(`Row ${index}: Email is required`);
    else if (!EMAIL_REGEX.test(email)) errors.push(`Row ${index}: Email "${email}" is invalid`);

    if (!firstName) errors.push(`Row ${index}: FirstName is required`);
    if (!lastName) errors.push(`Row ${index}: LastName is required`);

    return errors;
}

function parseCsv(file: File): Promise<{ headers: string[]; rows: Row[]; rowCount: number }> {
    return new Promise((resolve, reject) => {
        Papa.parse<Row>(file, {
            header: true,
            skipEmptyLines: "greedy",
            dynamicTyping: false,
            transformHeader: (header: string): string => transformHeaderClean(header),
            worker: false,
            complete: (results: Papa.ParseResult<Row>): void => {
                if (results.errors && results.errors.length > 0) {
                    const msg = results.errors.map((e: Papa.ParseError) => e.message).join("; ");
                    reject(new Error(msg));
                    return;
                }
                const headers: string[] = (results.meta.fields ?? []).map((h: string | undefined) => transformHeaderClean(h ?? ""));
                resolve({headers, rows: results.data, rowCount: results.data.length});
            },
            error: (err: unknown): void => {
                if (err instanceof Error) reject(err);
                else reject(new Error(String(err)));
            }
        });
    });
}

function isCsvMime(type: string): boolean {
    return type === "text/csv" || type === "" || type === "application/vnd.ms-excel";
}

async function validateCsvFile(file: File): Promise<string[]> {
    const errs: string[] = [];
    const extOk = /\.csv$/i.test(file.name);
    const typeOk = isCsvMime(file.type);
    if (!extOk) errs.push("File must have a .csv extension");
    if (!typeOk) errs.push(`Unexpected MIME type "${file.type || "unknown"}" (expected text/csv)`);
    if (file.size === 0) errs.push("File is empty");
    if (file.size > MAX_SIZE_MB * 1024 * 1024) errs.push(`File exceeds ${MAX_SIZE_MB}MB`);
    if (errs.length) return errs;

    let headers: string[] = [];
    let rows: Row[] = [];
    let rowCount = 0;

    try {
        const parsed = await parseCsv(file);
        headers = parsed.headers;
        rows = parsed.rows;
        rowCount = parsed.rowCount;
    } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        errs.push(`Failed to parse CSV: ${msg}`);
        return errs;
    }

    if (!headers.length) {
        errs.push("No headers found");
    } else if (!headersMatchStrict(headers)) {
        errs.push(
            `CSV headers do not match exactly.\n` +
            `Found: [${headers.join(", ")}]\n` +
            `Expected: [${expectedHeadersStrict.join(", ")}]`
        );
    }

    if (rowCount === 0) errs.push("No data rows found");

    if (errs.length === 0) {
        const rowErrors: string[] = [];

        rows.forEach((row: Row, i: number) => {
            const idx = i + 2;
            rowErrors.push(...validateRow(row, idx));
        });

        // Duplicate email check
        const seen = new Set<string>();
        rows.forEach((r: Row, i: number) => {
            const email = (r.Email ?? "").toLowerCase().trim();
            if (email) {
                if (seen.has(email)) rowErrors.push(`Row ${i + 2}: Duplicate Email "${email}"`);
                else seen.add(email);
            }
        });

        const MAX_ERRORS = 50;
        if (rowErrors.length > MAX_ERRORS) {
            errs.push(...rowErrors.slice(0, MAX_ERRORS));
            errs.push(`…and ${rowErrors.length - MAX_ERRORS} more errors`);
        } else {
            errs.push(...rowErrors);
        }
    }

    return errs;
}

function MemberBulkUpload({
                              onUpload,
                              setError,
                              uploading,
                              disabled,
                              error,
                              isEmptyFile,
                              clearError
                          }: MemberFileUploadProps) {

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const inputFile = useRef<HTMLInputElement | null>(null);

    const handleClick = (): void => {
        inputFile.current?.click();
    };

    const fileSelected = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
        clearError();
        setValidationErrors([]);
        const file: File | null = event.target.files?.item(0) ?? null;
        setSelectedFile(file);
        if (file) {
            const errs = await validateCsvFile(file);
            setValidationErrors(errs);
        }
    };

    async function handleUpload(): Promise<void> {
        clearError();
        setValidationErrors([]);
        if (!selectedFile) {
            setError("Please select a CSV file to upload.");
            return;
        }

        const errs = await validateCsvFile(selectedFile);
        if (errs.length) {
            setError(errs[0]);
            setValidationErrors(errs);
            return;
        }

        await onUpload(selectedFile);
        setSelectedFile(null);
        if (inputFile.current) inputFile.current.value = "";
    }

    return (
        <div className={`h-full p-4 lg:p-6 ${portalCardSurface}`}>
            <div className="mb-md text-left sm:text-center">
                <h3 className="font-sans-co font-bold text-portal-teal text-h3 mb-xs">Bulk upload</h3>
                <p className={portalPageSubtitle}>Update all members&apos; data by a single CSV file upload.</p>
            </div>
            <div className="flex flex-row items-center gap-sm">
                <input
                    type="file"
                    onChange={fileSelected}
                    hidden
                    accept=".csv,text/csv"
                    ref={inputFile}
                />
                <input
                    className={`${portalOutlineControl} mb-0 flex-1`}
                    type="text"
                    value={selectedFile?.name ?? ''}
                    placeholder="Select CSV file..."
                    onClick={handleClick}
                    readOnly
                />
                <img src={uploadIcon} alt="" className="h-lg flex-shrink-0" aria-hidden="true"/>
            </div>

            {isEmptyFile && <p className="text-p2 text-error-red">File is empty.</p>}
            {error && <p className="text-p2 text-error-red">{error}.</p>}

            {validationErrors.length > 0 && (
                <div className="mt-sm">
                    <ul className="list-disc ml-md text-error-red text-p2">
                        {validationErrors.map((e: string, i: number) => (
                            <li key={i}>{e}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="w-full flex flex-row items-center justify-around gap-lg mt-md ">
                <StateButton
                    submit={false}
                    className="text-pure-white bg-dark-grey"
                    disabled={disabled}
                    loading={uploading}
                    onClick={handleUpload}
                >
                    Upload
                </StateButton>
                <div>
                    <a href="/files/Member_File_Template.csv" download className={portalSecondaryPillButton}>
                        Download template
                    </a>
                </div>
            </div>
        </div>
    );
}

export default MemberBulkUpload;
